import type { MaterialAudience, PortalUserKind } from "../api/types";

// External Portal v2 §4.2 — type-driven tab matrix.
//
// Phase 2 shipped Requests + Documents + Account/Settings as REAL
// tabs for every recipient account type.
//
// Phase 3 (this file) appends the placeholder entries from the §4.2
// matrix, keyed off `AccountType`:
//
// | Tab                          | Broker  | Buyer   | Seller  | Vendor  |
// | ---------------------------- | ------- | ------- | ------- | ------- |
// | Requests                     | real    | real    | real    | real    |
// | Documents                    | real    | real    | real    | real    |
// | Payments / Ledger            | placeh. | placeh. | placeh. | hidden  |
// | Notifications                | placeh. | placeh. | placeh. | placeh. |
// | Income / SPV contributions   |   —     |   —     | placeh. |   —     |
// | Invoices                     |   —     |   —     |   —     | placeh. |
// | Annual market check          |   —     |   —     |   —     | real    |
// | Deals / pipeline             | real    | real    | real    |   —     |
// | Account & settings           | real    | real    | real    | real    |
//
// "Placeholder" tabs render a `<PlaceholderTab>` empty state with the
// `comingSoon` flag on the tab strip. No backend wiring — the user
// sees the surface and a one-line explanation of what will live there.

export type PortalTabId =
  | "home"
  | "requests"
  | "documents"
  | "files"
  | "loans"
  | "debt-collector-language"
  | "bills"
  | "distributions"
  | "commissions"
  | "collections"
  | "tax-forms"
  | "payments"
  | "notifications"
  | "contributions"
  | "invoices"
  | "payment-setup"
  | "deals"
  // Broker Commission Modeler — the broker's own what-if scratchpad for their
  // compensation. 🔴 NOT `commissions`: that id is the TPS firm's servicing-fee
  // income surface, and the Broker's `payments` tab already renders a
  // BrokerCommissionsTab. Three different things; three different ids.
  | "commission-modeler"
  // Partner Brand Ownership — the broker / brokerage view of the completions
  // their OWN prequalification site produced. Shared id: both lineups render
  // the same tab, and the server scopes it to whichever site resolves for the
  // authenticated account.
  | "prequalification"
  | "goals"
  | "reports"
  | "performance"
  // Annual Vendor Audit — the vendor's own copy of the yearly market check
  // Phoenix runs on their pricing: the comparison, the discussion, and every
  // past year's determination. Vendor-only.
  | "annual-audit"
  | "vendors"
  | "customers"
  // Brokerage / Sub-Broker portal — the firm's roster of affiliated sub-brokers
  // and a firm-wide activity feed. New ids (the rest of the lineup reuses the
  // shared recipient/broker tabs).
  | "roster"
  | "activity"
  // Brokerage / Sub-Broker PR-7 — the broker-side "Affiliation Requests" surface:
  // an invited broker accepts/declines a proposed firm affiliation.
  | "affiliations"
  // Lead Generation console — the external generator's four working sections,
  // driven by the shared black left rail (no in-page nav). Prefixed `lg-` so
  // they never collide with the recipient/TPS ids above.
  | "lg-preleads"
  | "lg-earnings"
  | "lg-feedback"
  | "settings";

export interface PortalTabSpec {
  id: PortalTabId;
  label: string;
  /** When true, the tab strip surfaces a "Coming soon" pill (handled
   *  by the design-system Tabs primitive) and the renderer is
   *  expected to render a <PlaceholderTab> with empty-state copy. */
  comingSoon?: boolean;
}

// Order matches §4.2 top-to-bottom; Account & settings always last.
// Journey-Stage Adaptive Dashboards — the stage-adaptive Home landing
// surface (journey spine + next best action + per-stage widgets). FIRST
// in EVERY account type's lineup, so it is the default landing tab
// (PortalDashboardShell falls back to tabs[0]).
const HOME: PortalTabSpec = { id: "home", label: "Home" };
const REQUESTS: PortalTabSpec = { id: "requests", label: "Requests" };
const DOCUMENTS: PortalTabSpec = { id: "documents", label: "Documents" };
// Post-Rollout PR 2 (PROB-20) — Files surfaces audience-tagged
// PortalMaterials (the admin Library) to the recipient/broker. Without
// it, Seller-tagged "Active" materials weren't reachable from any
// non-broker portal surface.
const FILES: PortalTabSpec = { id: "files", label: "Files & library" };
// External party portals PR-A/PR-B/PR-C — "Payments & ledger" is a REAL surface
// for Buyer (payments), Seller (income accounting) and Broker (commissions);
// each renders its own per-party view. Vendor has no payments tab. No recipient
// type uses a placeholder Payments tab anymore.
const PAYMENTS_REAL: PortalTabSpec = { id: "payments", label: "Payments & ledger" };
// Payment Setup — a guided, lay-person bank/ACH setup surface. Buyer connects
// the bank their loan payments come from; Seller/Broker onboard a Stripe payout
// account. Sits just before Notifications in each lineup.
const PAYMENT_SETUP: PortalTabSpec = { id: "payment-setup", label: "Payment setup" };
const NOTIFICATIONS: PortalTabSpec = { id: "notifications", label: "Notifications" };
const CONTRIBUTIONS: PortalTabSpec = { id: "contributions", label: "Income & SPV contributions", comingSoon: true };
// Vendor Billing v1.0 — Invoices promoted from placeholder to real for the
// Vendor account type (VFI invoice list + detail; online payment ships
// with the Stripe PR).
const INVOICES: PortalTabSpec = { id: "invoices", label: "Invoices" };
// External Portal v2 PR 2 (commit D) — Deals & status promoted from
// placeholder to real for Buyer/Seller/Broker. Read-only mirror of the
// staff pipeline, surfaced via /api/portal/deals/mine.
const DEALS: PortalTabSpec = { id: "deals", label: "Deals & status" };
// Partner Brand Ownership — the completions the caller's own prequalification
// site produced. Shown to Broker and Brokerage accounts; the tab renders its own
// "no site linked yet" empty state when no site resolves for the account.
const PREQUALIFICATION: PortalTabSpec = { id: "prequalification", label: "Prequalification" };
// Broker Commission Modeler — a private what-if scratchpad: sketch a deal and
// see what it earns the broker at closing and monthly.
// Sits beside the broker's other money surfaces. A JUNIOR broker keeps it: it
// is a modeling tool, not a payout surface, so none of the reasoning that hides
// Payment setup from them applies.
const COMMISSION_MODELER: PortalTabSpec = {
  id: "commission-modeler",
  label: "Commission Modeler",
};
// Vendor Program v2 PR 2 — the monthly cycle.
// Buyer: "My goals" (their improvement goals progressing + accepted-report
// sign-off). Vendor: "Reports" (per-assignment monthly filing) and
// "Performance" (their own ratings — never another vendor's).
const GOALS: PortalTabSpec = { id: "goals", label: "My goals" };
// 🔴 There is NO separate "Reporting" tab. Borrower report requests arrive in
// REQUESTS as an ordinary multi-item CollectionPackage — the same way a broker
// application bundle does — so the borrower has ONE inbox, not two. Do not
// reintroduce a second list surface for them.
const REPORTS: PortalTabSpec = { id: "reports", label: "Reports" };
const PERFORMANCE: PortalTabSpec = { id: "performance", label: "Performance" };
// Annual Vendor Audit — the yearly market check on the vendor's pricing, as the
// vendor reads it: what Phoenix pays, what competitors quoted, Phoenix's
// preliminary view, and a two-sided discussion they can answer in before any
// determination is made. Sits beside Performance (the other "how are we judging
// you" surface). 🔴 The label here and the heading in PortalTabHeader's TAB_META
// are two independent literals with nothing tying them together — keep them the
// same words.
const ANNUAL_AUDIT: PortalTabSpec = { id: "annual-audit", label: "Annual market check" };
// Vendor Connection v1 — the explicit two-sided connection. Buyer sees the
// vendors connected to their loan(s); vendor sees the connected customers'
// contact cards + per-loan work view.
const MY_VENDORS: PortalTabSpec = { id: "vendors", label: "My Vendors" };
const CUSTOMERS: PortalTabSpec = { id: "customers", label: "Customers" };
// TPS Servicing Portal PR-C — the third-party servicer's assigned-loans
// roster (its primary working surface).
const LOANS: PortalTabSpec = { id: "loans", label: "Loans" };
// TPS Servicing Portal PR-D — the firm's per-state borrower-facing
// debt-collector disclosure (review the primed default with counsel, then
// approve; an approved disclosure gates accepting loans in that state).
const DEBT_COLLECTOR_LANGUAGE: PortalTabSpec = {
  id: "debt-collector-language",
  label: "Debt-collector language",
};
// TPS Servicing Portal PR-E — the firm's review-and-approve queue for the
// monthly bills Phoenix prepares on its behalf. A bill is invisible to the
// borrower until the firm approves it.
const BILLS: PortalTabSpec = { id: "bills", label: "Bills" };
// TPS Servicing Portal PR-G — the firm's money tabs: the Distribution Ledger
// (approve each cleared payment's payout) and Commissions (earned servicing-fee
// income).
const DISTRIBUTIONS: PortalTabSpec = { id: "distributions", label: "Distributions" };
const COMMISSIONS: PortalTabSpec = { id: "commissions", label: "Commissions" };
// TPS Servicing Portal PR-H — the firm's Collections surface: its assigned
// default/collection cases (read), where it adds notes and records recoveries
// (earning the collection-recovery fee).
const COLLECTIONS: PortalTabSpec = { id: "collections", label: "Collections" };
// TPS Servicing Portal PR-I — the firm's 1099 management surface (the payer
// view: select → prefill → edit → approve → send → e-file) AND, for every
// recipient account type, a read-only "my forms" surface (the same tab id
// renders different components per account type).
const TAX_FORMS: PortalTabSpec = { id: "tax-forms", label: "Tax forms" };
// Lead Generation console — the external generator's working sections. Home is
// the shared HOME spec (its lineup leads with it); the other three are console-
// specific. Rendered by LeadGeneratorDashboardPage via the black rail's ?tab=.
const LG_PRELEADS: PortalTabSpec = { id: "lg-preleads", label: "Production line" };
const LG_EARNINGS: PortalTabSpec = { id: "lg-earnings", label: "Earnings" };
const LG_FEEDBACK: PortalTabSpec = { id: "lg-feedback", label: "Feedback" };
// Brokerage / Sub-Broker portal — the firm's roster of affiliated sub-brokers
// (their production) and a firm-wide activity feed. Real tabs (PR-6 fills the
// data surfaces); not placeholders in the spec sense.
const ROSTER: PortalTabSpec = { id: "roster", label: "Sub-brokers" };
const ACTIVITY: PortalTabSpec = { id: "activity", label: "Activity" };
// Brokerage / Sub-Broker PR-7 — the broker-side consent surface: an invited
// broker reviews a proposed firm affiliation and accepts (joins the firm,
// leaving any current one) or declines.
const AFFILIATIONS: PortalTabSpec = { id: "affiliations", label: "Affiliations" };
const SETTINGS: PortalTabSpec = { id: "settings", label: "Account & settings" };

/** Compute the visible tab list for a user. Driven by AccountType per
 *  §4.2; Kind is reserved for the broker carve-out the spec calls
 *  out for Phase 3.1 (the broker dashboard refactor — currently the
 *  broker still uses the legacy BrokerDashboardPage, so this lookup
 *  returns the recipient lineup for any Kind hitting it). */
export function tabsForUser(
  accountType: MaterialAudience,
  _kind: PortalUserKind,
  // Brokerage / Sub-Broker — a JUNIOR broker is paid through their covering
  // brokerage (off Phoenix's platform), so their own Payment-setup tab is
  // hidden. Optional + defaulted so every existing 2-arg caller is unchanged;
  // omitted / "Independent" / null keeps the full lineup.
  brokerType?: "Independent" | "Junior" | null,
): PortalTabSpec[] {
  switch (accountType) {
    case "Buyer":
      return [HOME, REQUESTS, GOALS, MY_VENDORS, DOCUMENTS, FILES, TAX_FORMS, DEALS, PAYMENTS_REAL, PAYMENT_SETUP, NOTIFICATIONS, SETTINGS];
    case "Seller":
      // Journey-Stage Adaptive Dashboards — Home leads the lineup (default
      // landing) for every external type, replacing the blank Requests landing.
      return [HOME, REQUESTS, DOCUMENTS, FILES, TAX_FORMS, DEALS, PAYMENTS_REAL, PAYMENT_SETUP, NOTIFICATIONS, CONTRIBUTIONS, SETTINGS];
    case "Vendor":
      // §4.2 — Payments hidden for Vendor; Invoices shown. No
      // deals tab for Vendor. Home leads (journey-stage adaptive dashboard).
      // Annual Vendor Audit — the yearly market check follows Performance: both
      // answer "how is Phoenix judging us", one on service and one on price.
      return [HOME, REQUESTS, REPORTS, CUSTOMERS, DOCUMENTS, TAX_FORMS, NOTIFICATIONS, INVOICES, PERFORMANCE, ANNUAL_AUDIT, SETTINGS];
    case "Broker": {
      // PR-7 — Affiliations (the firm-invite consent surface) sits right after
      // Requests. The rest is the established broker lineup. A JUNIOR broker is
      // paid through their covering brokerage, so the Payment-setup (own payout
      // onboarding) tab is hidden — keyed strictly on BrokerType, never on
      // affiliation, so an Independent broker (affiliated or not) is unchanged.
      const brokerTabs = [HOME, REQUESTS, AFFILIATIONS, DOCUMENTS, FILES, TAX_FORMS, DEALS, PREQUALIFICATION, PAYMENTS_REAL, COMMISSION_MODELER, PAYMENT_SETUP, NOTIFICATIONS, SETTINGS];
      return brokerType === "Junior"
        ? brokerTabs.filter((t) => t.id !== "payment-setup")
        : brokerTabs;
    }
    case "Tps":
      // TPS Servicing Portal PR-C/PR-D/PR-E/PR-G/PR-H/PR-I — the servicer's
      // replicated tabs plus the Loans roster, the Debt-collector language editor,
      // the Bills review-and-approve queue, the money tabs (Distribution Ledger +
      // Commissions), Collections (its default/collection cases), and the 1099 Tax
      // forms management surface. The remaining regulatory tabs ship in later PRs.
      return [
        HOME,
        REQUESTS,
        LOANS,
        // TPS Stripe onboarding (#7b/#9) — the firm's self-serve Stripe Connect
        // "Payment setup" surface, right after its Loans roster.
        PAYMENT_SETUP,
        DEBT_COLLECTOR_LANGUAGE,
        BILLS,
        DISTRIBUTIONS,
        COMMISSIONS,
        COLLECTIONS,
        TAX_FORMS,
        DOCUMENTS,
        FILES,
        NOTIFICATIONS,
        SETTINGS,
      ];
    case "LeadGenerator":
      // Lead Generation console — the four working sections now live IN the
      // shared black left rail (one nav, no in-page strip). The dedicated
      // LeadGeneratorDashboardPage maps each ?tab= id to the section body.
      return [HOME, LG_PRELEADS, LG_EARNINGS, LG_FEEDBACK];
    case "Brokerage":
      // Brokerage / Sub-Broker portal — the firm's dedicated lineup: its Home
      // landing, the Deals & Payments surfaces, PR-6b's Payment setup (Stripe
      // payout onboarding, adjacent to Payments), the affiliated-sub-broker
      // Roster and a firm-wide Activity feed, then the shared recipient tabs. The
      // dedicated BrokerageDashboardPage maps each ?tab= id to its body.
      return [HOME, DEALS, PREQUALIFICATION, PAYMENTS_REAL, PAYMENT_SETUP, ROSTER, ACTIVITY, REQUESTS, DOCUMENTS, FILES, NOTIFICATIONS, SETTINGS];
    case "All":
    default:
      // "All" is not valid on a PortalUser row; fall through to the
      // safest minimum lineup so the shell still renders.
      return [REQUESTS, DOCUMENTS, SETTINGS];
  }
}

/** Last-resort fallback tab id. Normally unused — every lineup leads with
 *  `home` and PortalDashboardShell lands on `tabs[0]`; this only applies if a
 *  lineup ever came back empty. */
export const DEFAULT_TAB: PortalTabId = "requests";
